<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { svgIcon } from "@/common/components";
// types
import type { ReportType, ReportBlockType } from "@/common/types/report";
// editors
import BlockEditor from "./editors/block-editor.vue";
import EmptyEditor from "./editors/empty.vue";
import MenuEditor from "./editors/menu.vue";
import TextEditor from "./editors/text-editor.vue";
import TableEditor from "./editors/table-editor.vue";

interface MenusType {
  title: string;
  component: any;
  props: Record<string, any>;
}

const props = defineProps<{
  type: string | null;
  block?: ReportBlockType | null;
  report: ReportType | null;
}>();

const emit = defineEmits([
  "close",
  "block:add",
  "block:update",
  "report:reload",
  "report:update",
  "select:type",
  "page:add",
]);

const internalType = ref(props.type || "empty");

const menus = computed(() => ({
  empty: {
    title: "Empty",
    component: EmptyEditor,
    props: {
      report: props.report,
    },
  },
  add: {
    title: "Adicionar Bloco",
    component: MenuEditor,
    props: {
      block: props.block,
    },
  },
  "block-text": {
    title: "Editar Texto",
    component: TextEditor,
    props: {
      block: props.block,
    },
  },
  "block-title": {
    title: "Editar Título",
    component: TextEditor,
    props: {
      block: props.block,
    },
  },
  "block-table": {
    title: "Editar Tabela",
    component: TableEditor,
    props: {
      block: props.block,
      report: props.report,
    },
  },
  "block-data": {
    title: "Editar Bloco",
    component: BlockEditor,
    props: {
      block: props.block,
      report: props.report,
    },
  },
}));

const selectedMenu = computed<MenusType | null>(
  () => menus.value[internalType.value as never],
);

function blockAdd(type: string) {
  emit("block:add", type);
}

function blockUpdate(block: ReportBlockType) {
  emit("block:update", block);
}

function clickEmptyMenu(key: "add" | "page") {
  if (key === "add") {
    emit("select:type", key);
    return;
  }

  if (key === "page") {
    emit("page:add");
    return;
  }

  emit("select:type", key);
}

watch(
  () => props.type,
  (value) => {
    internalType.value = value || "empty";
  },
);
</script>

<template>
  <div class="menu">
    <template v-if="internalType === 'empty'">
      <div class="menu-content">
        <component
          v-bind="menus.empty.props"
          :is="menus.empty.component"
          @click:menu="clickEmptyMenu"
          @report:update="emit('report:update', $event)"
          @report:reload="emit('report:reload')"
        />
      </div>
    </template>

    <template v-else-if="internalType === 'add'">
      <div class="title">
        {{ menus[internalType].title }}
        <button class="close-button" @click="emit('close')">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>

      <div class="menu-content">
        <component
          internalBlock.value.content.table
          :is="menus[internalType].component"
          @block:add="blockAdd"
        />
      </div>
    </template>

    <template v-else>
      <template v-if="selectedMenu">
        <div class="title">
          {{ selectedMenu!.title }}
          <button class="close-button" @click="emit('close')">
            <svg-icon icon="close" size="1.25rem" />
          </button>
        </div>

        <div class="menu-content">
          <component
            :is="selectedMenu!.component"
            v-bind="selectedMenu!.props"
            @block:update="blockUpdate"
          />
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.menu {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;

  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px;
    border-bottom: 1px solid #ccc;
    font-size: 15px;
    font-weight: 600;
    background: #e4e4e4;

    button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
    }
  }

  .menu-content {
    display: flex;
    flex-direction: column;
    overflow: auto;
  }
}
</style>
